import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Module-level cache so the config is fetched once per session.
let _cache = null;
let _pending = null;

async function fetchOnce() {
  if (_cache) return _cache;
  if (_pending) return _pending;
  _pending = api.get("/feature-access")
    .then((r) => { _cache = r.data; return _cache; })
    .catch(() => { _cache = null; return null; })
    .finally(() => { _pending = null; });
  return _pending;
}

export function invalidateFeatureAccessCache() { _cache = null; _pending = null; }

/**
 * Hook exposing feature/quota lookups for the current user.
 *
 * canAccess(featureId) → boolean (true when allowed OR user has bypass role)
 * getQuota(quotaId)    → number | null (null = unlimited; also for bypass)
 * planLabel            → "1 Bulan" / "6 Bulan" / "Premium"
 * config               → raw config { features, quotas } (useful for admin UI)
 */
export function useFeatureAccess() {
  const { user } = useAuth();
  const [data, setData] = useState(_cache);

  useEffect(() => {
    let alive = true;
    if (!user) return;
    fetchOnce().then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, [user]);

  const isBypass = !!data?.bypass || user?.role === "sysadmin" || user?.role === "Admin";
  const plan = data?.plan || user?.plan || "none";

  const canAccess = useCallback((featureId) => {
    if (isBypass) return true;
    const per = data?.config?.features?.[featureId];
    if (!per) return true; // unknown feature → allow
    return !!per[plan];
  }, [isBypass, data, plan]);

  const getQuota = useCallback((quotaId) => {
    if (isBypass) return null; // unlimited
    const per = data?.config?.quotas?.[quotaId];
    if (!per) return null;
    const v = per[plan];
    return v === undefined ? null : v; // null = unlimited
  }, [isBypass, data, plan]);

  const planLabel = { bulan1: "Paket 1 Bulan", bulan6: "Paket 6 Bulan", premium: "Paket Premium" }[plan] || "Paket tidak aktif";

  return {
    ready: !!data || !user,
    config: data?.config || null,
    plan, planLabel, isBypass,
    canAccess, getQuota,
  };
}
